import { useState } from 'react';
import { CheckCircle, Users, Target, Award, ChevronDown, ChevronUp, User } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';

const AboutPage = () => {
    const { t, language } = useLanguage();
    const [showTeam, setShowTeam] = useState(false);
    const [showOrgTeam, setShowOrgTeam] = useState(false);

    // Organization Team Data - arranged in pyramid: 1 + 2
    const teamRow1 = [
        { 
            name: 'Baran Topçil', 
            role: 'Founder & President', 
            photo: '/team/1.jpg', 
            isFounder: true 
        }
    ];
    
    const teamRow2 = [
        { 
            name: 'Efecan Tufan', 
            role: 'Chief of Academy', 
            photo: '/team/8.jpg'
        },
        { 
            name: 'Utku Yılmaz', 
            role: 'Chief of Academy', 
            photo: '/team/7.jpg'
        }
    ];

    // Organization Team - Chiefs and Advisors
    const orgChiefs = [
        { 
            name: 'Kayra Yağız Bektaş', 
            role: 'Chief of Organization', 
            photo: '/team/2.jpg'
        },
        { 
            name: 'İlayda Yıldız', 
            role: 'Chief of Organization', 
            photo: '/team/6.jpg'
        }
    ];

    const orgAdvisors = [
        { 
            name: 'Kayra Acımış', 
            role: 'Organization Advisor', 
            photo: '/team/3.jpg'
        },
        { 
            name: 'Ada Su Yılmaz', 
            role: 'Organization Advisor', 
            photo: '/team/4.jpg'
        },
        { 
            name: 'Aybars Demir Gültekin', 
            role: 'Organization Advisor', 
            photo: '/team/5.jpg'
        }
    ];

    // 9 Committees for poster grid (UNESCO removed)
    const committeePosters = [
        { id: 'sochum', name: 'SOCHUM', poster: 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/apa0z5ai_WhatsApp%20Image%202026-02-13%20at%2009.38.25.jpeg' },
        { id: 'disec', name: 'DISEC', poster: 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/w8uo144k_WhatsApp%20Imag%202026-02-13%20at%2009.38.28.jpeg' },
        { id: 'ecosoc', name: 'ECOSOC', poster: 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/898c5j8o_WhatsApp%20Image%202026-0213%20at%2009.38.25.jpe.jpeg' },
        { id: 'fia', name: 'FIA', poster: 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/fn0kgxsk_WhatsApp%20Imag%202026-02-13%20at%2009.38.26.jpeg' },
        { id: 'series', name: 'SERIES', poster: 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/c3o6eza9_WhatsApp%20Image%202026-0213%20at%2009.38.27.jpeg' },
        { id: 'h-specpol', name: 'H-SPECPOL', poster: 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/tpx8p84m_WhatsApp%20Iage%202026-02-13%20at%2009.38.28.jpeg' },
        { id: 'tbmm', name: 'TBMM', poster: 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/9x88y35m_WhatsApp%20Image%202s026-02-14%20at%2011.32.01.jpeg' },
        { id: 'oligarchy', name: 'Oligarchy', poster: 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/q5bizkaq_WhatsApp%20Iage%202026-02-13%20at%2009.38.29.jpeg' },
        { id: 'ottoman-dynasty', name: 'Ottoman Dynasty', poster: 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/iusi6ao3_8.jpeg' }
    ];

    const values = [
        {
            icon: Target,
            title: t('about.excellence'),
            description: t('about.excellenceDesc')
        },
        {
            icon: Users,
            title: t('about.collaboration'),
            description: t('about.collaborationDesc')
        },
        {
            icon: Award,
            title: t('about.integrity'),
            description: t('about.integrityDesc')
        }
    ];

    const features = [
        t('about.features.trainedSecretariat'),
        t('about.features.experiencedChairs'),
        t('about.features.qualityGuides'),
        t('about.features.delegateTraining'),
        t('about.features.networking'),
        t('about.features.certificate')
    ];

    // Stats data - 9 committees now (UNESCO removed)
    const stats = [
        { number: '9', label: language === 'tr' ? 'Komite' : 'Committees' },
        { number: '500+', label: language === 'tr' ? 'Beklenen Delege' : 'Expected Delegates' },
        { number: '3', label: language === 'tr' ? 'Konferans Günü' : 'Conference Days' }
    ];

    // Team Card Component
    const TeamCard = ({ member, isFounder = false, isChief = false }) => (
        <div className={`bg-white rounded-xl overflow-hidden transition-all duration-300 hover:shadow-elegant-lg ${
            isFounder 
                ? 'border-2 border-accent shadow-2xl w-56 sm:w-72' 
                : isChief
                    ? 'border-2 border-accent/50 shadow-lg w-40 sm:w-64'
                    : 'border border-border hover:border-accent/30 w-28 sm:w-60'
        }`}>
            <div className={`${isFounder ? 'h-48 sm:h-72' : isChief ? 'h-40 sm:h-64' : 'h-36 sm:h-64'} bg-white overflow-hidden relative flex items-start justify-center`}>
                {member.photo ? (
                    <img 
                        src={member.photo} 
                        alt={member.name || 'Team Member'}
                        className="w-auto h-auto max-w-full max-h-full object-contain"
                        style={{ marginTop: '0' }}
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <User className={`${isFounder ? 'w-16 sm:w-20 h-16 sm:h-20' : 'w-10 sm:w-14 h-10 sm:h-14'} text-primary/30`} />
                    </div>
                )}
            </div>
            <div className={`${isFounder ? 'p-3 sm:p-5 bg-gradient-to-b from-white to-accent/5' : 'p-2 sm:p-4'} text-center`}>
                <h4 className={`font-display font-semibold text-foreground mb-0.5 sm:mb-1 ${isFounder ? 'text-base sm:text-xl font-extrabold' : isChief ? 'text-sm sm:text-lg font-bold' : 'text-xs sm:text-base'}`}>
                    {member.name || (language === 'tr' ? 'İsim Soyisim' : 'Name Surname')}
                </h4>
                <p className={`text-accent font-medium leading-tight ${isFounder ? 'text-xs sm:text-base' : 'text-[10px] sm:text-sm'}`}>
                    {member.role || (language === 'tr' ? 'Görev' : 'Position')}
                </p>
            </div>
        </div>
    );

    return (
        <>
            {/* Hero Section */}
            <section className="pt-32 pb-20 bg-primary">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl">
                        <p className="text-accent font-semibold text-sm uppercase tracking-wider mb-4">
                            {t('about.subtitle')}
                        </p>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
                            {t('about.title')}
                        </h1>
                        <p className="text-xl text-primary-foreground/80 leading-relaxed">
                            {t('about.description')}
                        </p>
                    </div>
                </div>
            </section>

            {/* Girne MUN Nedir Section */}
            <section className="py-20 bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-4xl mx-auto text-center mb-16">
                        <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-6 gold-underline">
                            {language === 'tr' ? 'Girne MUN 2026 Nedir?' : 'What is Girne MUN 2026?'}
                        </h2>
                        <p className="text-lg text-muted-foreground leading-relaxed">
                            {language === 'tr' 
                                ? "Girne Model United Nations 2026, Türkiye'nin dört bir yanından gelen 500+ delegeyi diplomasi, liderlik ve küresel perspektif çerçevesinde bir araya getiren prestijli bir akademik konferanstır. 3 gün boyunca 9 komitede gerçekleşecek simülasyonlar ile katılımcılar gerçek Birleşmiş Milletler atmosferini deneyimler."
                                : "Girne Model United Nations 2026 is a prestigious academic conference that brings together 500+ delegates from all over Turkey within the framework of diplomacy, leadership and global perspective. With simulations in 9 committees over 3 days, participants experience the real United Nations atmosphere."
                            }
                        </p>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-8 max-w-3xl mx-auto">
                        {stats.map((stat, index) => (
                            <div key={index} className="text-center">
                                <p className="font-display text-4xl sm:text-5xl font-bold text-accent mb-2">
                                    {stat.number}
                                </p>
                                <p className="text-muted-foreground font-semibold">
                                    {stat.label}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Organization Team Section */}
            <section className="py-20 section-cream">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-6 gold-underline">
                            {language === 'tr' ? 'Organizasyon Ekibi' : 'Organization Team'}
                        </h2>
                        <Button
                            onClick={() => setShowTeam(!showTeam)}
                            className="bg-gradient-to-r from-accent to-accent/80 hover:from-accent/90 hover:to-accent/70 text-primary font-semibold px-8 py-3 rounded-lg"
                        >
                            {language === 'tr' ? 'Sekretaryayı Keşfet' : 'Discover the Secretariat'}
                            {showTeam ? (
                                <ChevronUp className="w-5 h-5 ml-2" />
                            ) : (
                                <ChevronDown className="w-5 h-5 ml-2" />
                            )}
                        </Button>
                    </div>

                    {/* Team Grid - Pyramid Layout */}
                    <div className={`transition-all duration-500 overflow-hidden ${showTeam ? 'max-h-[2000px] opacity-100 mt-10' : 'max-h-0 opacity-0'}`}>
                        <div className="flex flex-col items-center px-2 sm:px-4">
                            {/* Row 1 - Baran (Founder) */}
                            <div className="flex justify-center items-end mb-6 sm:mb-8">
                                {teamRow1.map((member, index) => (
                                    <TeamCard key={`row1-${index}`} member={member} isFounder={true} />
                                ))}
                            </div>
                            
                            {/* Row 2 - 2 People */}
                            <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-center sm:items-end sm:gap-10 -mt-2 max-w-md mx-auto sm:max-w-none">
                                {teamRow2.map((member, index) => (
                                    <div key={`row2-${index}`} className="flex justify-center">
                                        <TeamCard member={member} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Organizasyon Ekibini Keşfet Button */}
                    <div className="text-center mt-12">
                        <Button
                            onClick={() => setShowOrgTeam(!showOrgTeam)}
                            variant="outline"
                            className="border-2 border-accent text-accent hover:bg-accent hover:text-primary font-semibold px-8 py-3 rounded-lg"
                        >
                            {language === 'tr' ? 'Organizasyon Ekibini Keşfet' : 'Discover Organization Team'}
                            {showOrgTeam ? (
                                <ChevronUp className="w-5 h-5 ml-2" />
                            ) : (
                                <ChevronDown className="w-5 h-5 ml-2" />
                            )}
                        </Button>
                    </div>

                    {/* Organization Team - Chiefs and Advisors */}
                    <div className={`transition-all duration-500 overflow-hidden ${showOrgTeam ? 'max-h-[2000px] opacity-100 mt-10' : 'max-h-0 opacity-0'}`}>
                        <div className="flex flex-col items-center px-2 sm:px-4">
                            {/* Chiefs of Organization - 2 side by side */}
                            <div className="mb-6 sm:mb-8 w-full">
                                <h3 className="text-center font-display text-lg sm:text-xl font-bold text-foreground mb-4 sm:mb-6">
                                    {language === 'tr' ? 'Chiefs of Organization' : 'Chiefs of Organization'}
                                </h3>
                                <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-center sm:items-end sm:gap-8 max-w-md mx-auto sm:max-w-none">
                                    {orgChiefs.map((member, index) => (
                                        <div key={`chief-${index}`} className="flex justify-center">
                                            <TeamCard member={member} isChief={true} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Organization Advisors - 3 side by side */}
                            <div className="mt-4 w-full">
                                <h3 className="text-center font-display text-lg sm:text-xl font-bold text-foreground mb-4 sm:mb-6">
                                    {language === 'tr' ? 'Organization Advisors' : 'Organization Advisors'}
                                </h3>
                                <div className="grid grid-cols-3 gap-1 sm:flex sm:justify-center sm:items-end sm:gap-6 max-w-md mx-auto sm:max-w-none">
                                    {orgAdvisors.map((member, index) => (
                                        <div key={`advisor-${index}`} className="flex justify-center">
                                            <TeamCard member={member} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Academic Excellence Section */}
            <section className="py-20 bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-4xl mx-auto text-center mb-12">
                        <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-6 gold-underline">
                            {language === 'tr' ? 'Akademik Mükemmellik' : 'Academic Excellence'}
                        </h2>
                        <p className="text-lg text-muted-foreground leading-relaxed">
                            {language === 'tr'
                                ? 'Girne MUN Akademi Ekibi, komite içeriklerini uluslararası MUN standartlarına uygun olarak tasarlamakta ve delegelere üst düzey bir tartışma deneyimi sunmaktadır.'
                                : 'The Girne MUN Academy Team designs committee content in accordance with international MUN standards and offers delegates a high-level debate experience.'
                            }
                        </p>
                    </div>

                    {/* 9 Committee Poster Grid */}
                    <div className="grid grid-cols-3 gap-6 max-w-5xl mx-auto mt-10">
                        {committeePosters.map((committee) => (
                            <div 
                                key={committee.id}
                                className="group relative aspect-[3/4] bg-primary rounded-xl overflow-hidden cursor-pointer transition-transform duration-300 hover:scale-105"
                            >
                                {/* Poster Image or Placeholder */}
                                {committee.poster ? (
                                    <>
                                        <img 
                                            src={committee.poster}
                                            alt={`${committee.name} Poster`}
                                            className="w-full h-full object-cover"
                                        />
                                        {/* Overlay on hover */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                                            <span className="text-white font-display font-bold text-xl">
                                                {committee.name}
                                            </span>
                                        </div>
                                    </>
                                ) : (
                                    <div className="absolute inset-0 bg-gradient-to-br from-primary to-primary/80 flex flex-col items-center justify-center text-center p-4">
                                        <span className="font-display text-4xl font-bold text-accent mb-3">
                                            {committee.name.slice(0, 2)}
                                        </span>
                                        <span className="text-primary-foreground font-semibold text-lg">
                                            {committee.name}
                                        </span>
                                        <span className="text-primary-foreground/60 text-sm mt-2">
                                            {language === 'tr' ? 'Poster Yakında' : 'Poster Coming Soon'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Features Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
                        {features.map((feature, index) => (
                            <div 
                                key={index} 
                                className="flex items-start gap-3 p-4 rounded-lg bg-card border border-border"
                            >
                                <CheckCircle className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                                <span className="text-muted-foreground">{feature}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Values Section */}
            <section className="py-20 section-cream">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <p className="text-accent font-semibold text-sm uppercase tracking-wider mb-3">
                            {t('about.foundation')}
                        </p>
                        <h2 className="font-display text-4xl font-bold text-foreground gold-underline">
                            {t('about.coreValues')}
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
                        {values.map((value, index) => (
                            <div
                                key={index}
                                className="p-8 rounded-xl bg-card border border-border hover:border-accent/30 transition-all duration-300 hover:shadow-elegant-lg text-center"
                            >
                                <div className="w-16 h-16 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-6">
                                    <value.icon className="w-8 h-8 text-accent" />
                                </div>
                                <h3 className="font-display text-2xl font-semibold text-foreground mb-4">
                                    {value.title}
                                </h3>
                                <p className="text-muted-foreground leading-relaxed">
                                    {value.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Venue Preview Section - REMOVED */}
        </>
    );
};

export default AboutPage;
